import { DisinfectionFilterOptions } from "gql/graphql";
import { AutoCompleteFilter } from "./filters/AutoCompleteFilter";
import { InterruptionFilter } from "./filters/InterruptionFilter";
import { ModeFilter } from "./filters/ModeFilter";
import { OrganizationCountryFilter } from "./filters/OrganizationCountryFilter";
import { OrganizationFilter } from "./filters/OrganizationFilter";
import { RobotTypeFilter } from "./filters/RobotTypeFilter";
import { StatusFilter } from "./filters/StatusFilter";
import { FilterList } from "types/types";
import { interruptionMsg } from "trans/definedMessages/interruptionMessages";
import { useIntl } from "react-intl";

export type onChangeFilter = {
  onChange: (value: any, category: string, checked?: boolean) => void;
};

interface Props {
  filterListLabels: any;
  filterOptions: any;
  supportFilterOptions?: any;

  id: keyof DisinfectionFilterOptions;
  filterList: FilterList;
}

export const getFilterComponent = ({
  id,
  filterListLabels,
  filterOptions,
  supportFilterOptions,
  onChange,
  filterList,
}: Props & onChangeFilter) => {
  const intl = useIntl();

  //LSBRI - TODO: This mapping funny business should be refactored out
  //              in favor of all values having a corresponding translation
  //              id directly attached at all times.
  //              (This applies to the entire application when we add
  //               translation support)
  const getInterruptionKeyFromTranslated = (translatedValue: string) => {
    return Object.keys(interruptionMsg).find(
      (key) =>
        intl.formatMessage(
          interruptionMsg[key as keyof typeof interruptionMsg],
        ) === translatedValue,
    );
  };

  switch (id) {
    case "serialNumbers":
      return (
        <AutoCompleteFilter
          key="serialNumbers"
          id="serialNumbers"
          selected={filterListLabels.includes("serialNumbers")}
          options={filterOptions?.serialNumbers || []}
          value={
            filterList.find((item: any) => item.label === "serialNumbers")
              ?.value || []
          }
          label="Robot"
          onChange={onChange}
        />
      );
    case "rooms":
      const rooms = (
        filterList.find((item: any) => item.label === "rooms")?.value || []
      ).map((item: any) => {
        if (item === "") {
          return "Current location";
        }
        return item;
      });
      const roomOptions =
        filterOptions?.rooms.map((o: any) =>
          o == "" ? "Current location" : o,
        ) || [];
      return (
        <AutoCompleteFilter
          key="rooms"
          id="rooms"
          selected={filterListLabels.includes("rooms")}
          options={[...new Set<string>(roomOptions)]}
          autoCompleteProps={{
            renderOption: (props: any, option: string) => (
              <li
                {...props}
                style={{
                  fontStyle:
                    option === "Current location" ? "italic" : "normal",
                }}
              >
                {option}
              </li>
            ),
          }}
          value={[...new Set<string>(rooms)]}
          label="Room"
          onChange={onChange}
        />
      );
    case "departments":
      const departments =
        filterList.find((item: any) => item.label === "departments")?.value ||
        [];
      return (
        <AutoCompleteFilter
          key="departments"
          id="departments"
          selected={filterListLabels.includes("departments")}
          value={departments.map((item: any) => {
            if (item === "") {
              return "No Department";
            }
            return item;
          })}
          onChange={onChange}
          options={
            filterOptions?.departments.map((o: any) =>
              o == "" ? "No Department" : o,
            ) || []
          }
          autoCompleteProps={{
            renderOption: (props: any, option: string) => (
              <li
                {...props}
                style={{
                  fontStyle: option === "No Department" ? "italic" : "normal",
                }}
              >
                {option}
              </li>
            ),
          }}
          label="Department"
        />
      );
    case "operators":
      return (
        <AutoCompleteFilter
          key="operators"
          id="operators"
          selected={filterListLabels.includes("operators")}
          options={filterOptions?.operators || []}
          value={
            filterList.find((item: any) => item.label === "operators")?.value ||
            []
          }
          label="Operator"
          onChange={onChange}
        />
      );
    case "disinfectionModes":
      return (
        <ModeFilter
          key="disinfectionModes"
          selected={filterListLabels.includes("disinfectionModes")}
          value={
            filterList.find((item: any) => item.label === "disinfectionModes")
              ?.value
          }
          onChange={onChange}
          options={filterOptions?.disinfectionModes || []}
        />
      );
    case "disinfectionStatus":
      return (
        <StatusFilter
          key="disinfectionStatus"
          selected={filterListLabels.includes("disinfectionStatus")}
          value={
            filterList.find((item: any) => item.label === "disinfectionStatus")
              ?.value || null
          }
          onChange={onChange}
          options={filterOptions?.disinfectionStatus || []}
        />
      );
    case "organizations":
      return (
        <OrganizationFilter
          key="organizations"
          selected={filterListLabels.includes("organizationIds")}
          value={
            filterList.find((item: any) => item.label === "organizationIds")
              ?.value || []
          }
          options={supportFilterOptions?.organizations || []}
          onChange={onChange}
        />
      );
    case "organizationCountries":
      return (
        <OrganizationCountryFilter
          key="organizationCountries"
          selected={filterListLabels.includes("organizationCountries")}
          value={
            filterList.find(
              (item: any) => item.label === "organizationCountries",
            )?.value || []
          }
          onChange={onChange}
        />
      );
    case "robotTypes":
      return (
        <RobotTypeFilter
          key="robotTypes"
          selected={filterListLabels.includes("robotTypes")}
          value={
            filterList.find((item: any) => item.label === "robotTypes")?.value
          }
          onChange={onChange}
          options={supportFilterOptions?.robotTypes || []}
        />
      );
    case "interruptionReasons":
      return (
        <InterruptionFilter
          key="interruptionReasons"
          selected={filterListLabels.includes("interruption")}
          value={
            filterList
              .find((item: any) => item.label === "interruption")
              ?.value?.reasons.map((o: string) =>
                intl.formatMessage(
                  interruptionMsg[o as keyof typeof interruptionMsg],
                ),
              ) || []
          }
          onChange={(value: any) => {
            onChange(
              value
                .map((v: any) => getInterruptionKeyFromTranslated(v))
                .filter(Boolean),
              "interruptionReasons",
            );
          }}
          options={
            supportFilterOptions?.interruptionReasons.map((o: string) =>
              intl.formatMessage(
                interruptionMsg[o as keyof typeof interruptionMsg],
              ),
            ) || []
          }
        />
      );
    default:
      return null;
  }
};
